import {
    useCallback, useMemo,
} from 'react'
import isEqual from 'lodash/isEqual'
import useSyncState from 'app/hooks/useSyncState'

import SkyNetSpreadSheet, {
    SkyNetSpreadSheetControlPanel,
    useTypeaheadOptions,
} from 'app/shared-components/SkyNetSpreadSheet'
import useLabel from 'app/hooks/useLabel'
import useEnumValues from 'app/hooks/useEnumValues'
import {
    FilterContext, FilterContextType, FilteredData,
} from 'app/hooks/useFilter'
import {
    useLocationAirportLoadOptions,
    standardLocationConvertionFn,
} from 'app/shared-components/LocationSelector'
import useDelayedLoadOptions from 'app/hooks/useDelayedLoadOptions'

// requests
import locationsRequests from 'app/requests/Location.requests'
import {
    countrySelectionConfig,
} from 'app/requests/Country.requests'
import {
    userSelectionConfig,
} from 'app/requests/User.requests'
import {
    contactSelectionConfig,
} from 'app/requests/Contact/Contact.requests'

import DataContentWrapper from 'app/shared-components/DataContentWrapper'
import {
    showLTL, showFTL, isDeliveryRoad, isCollection,
} from 'app/Apps/Pricing/utils/utils'
import {
    AsyncMutationOptions,
} from 'app/types/request.types'
import {
    AddressCategory,
    LocationType,
} from 'app/enums/label.enums'

import {
    AccountLane,
    LaneBulkCreatePayload,
} from '../AccountLanes.types'

import getConfig, {
    AccountLaneSpreadsheetType,
} from './AccountLaneSpreadsheet.config'
import filterConfig from './AccountLaneSpreadsheet.filterConfig'

const AccountLaneSpreadsheet = ({
    onSave,
    onCancel,
    filterOptions,
}: {
    onSave: (v: LaneBulkCreatePayload, o: AsyncMutationOptions) => void
    onCancel: (...args: any[]) => void,
    filterOptions: FilterContextType,
}) => {
    const {
        value, setValue,
    } = useSyncState(filterOptions?.filteredData as FilteredData<AccountLaneSpreadsheetType[]>)

    const getLabel = useLabel()
    const getAllowedValues = useEnumValues()

    const handleSave = useCallback(async (options?: AsyncMutationOptions) => {
        const lanes = value.map((lane: AccountLaneSpreadsheetType) => {
            return {
                ...lane,
                ...(lane?.isTemperatureControlled || []).reduce((acc, key) => {
                    return {
                        ...acc,
                        [key]: true,
                    }
                }, {}),
                isTemperatureControlled: undefined,
            }
        })

        return onSave({
            account: {
                id: filterOptions?.filter?.accountId[0],
            },
            lanes,
        }, options)
    }, [
        filterOptions?.filter?.accountId,
        onSave,
        value,
    ])

    const filterByAccount = useMemo(() => {
        return {
            accountId: filterOptions?.filter?.accountId,
        }
    }, [filterOptions?.filter])

    const loadLocationAirportOptions = useLocationAirportLoadOptions()
    const locationAirportParams = useTypeaheadOptions({
        load: loadLocationAirportOptions,
    })

    const loadCountryOptions = useDelayedLoadOptions(countrySelectionConfig)
    const countryParams = useTypeaheadOptions({
        load: loadCountryOptions,
    })

    const loadContactOptions = useDelayedLoadOptions(contactSelectionConfig)
    const contactParams = useTypeaheadOptions({
        load: loadContactOptions,
    })

    const loadUserOptions = useDelayedLoadOptions(userSelectionConfig)
    const userParams = useTypeaheadOptions({
        load: loadUserOptions,
    })

    const customerExecutionOptions = useMemo(() => {
        return {
            ...locationsRequests.selector,
            convertion: standardLocationConvertionFn,
            includeFilters: {
                locationType: [LocationType.ADDRESS],
                addressCategory: [
                    AddressCategory.CUSTOMER,
                    AddressCategory.CUSTOMER_QUOTATION,
                ],
            },
        }
    }, [])
    const loadCustomerExecutionOptions = useDelayedLoadOptions(customerExecutionOptions)
    const customerExecutionParams = useTypeaheadOptions({
        load: loadCustomerExecutionOptions,
    })

    const isNotDeliveryByRoad = useCallback((lane: AccountLane) => {
        return !isDeliveryRoad(lane.deliveryServiceType)
    }, [])

    const isNoTransportModeRoad = useCallback((lane: AccountLane) => {
        return !lane.transportModeRoad
    }, [])

    const isNotCollection = useCallback((lane: AccountLane) => {
        return !isCollection(lane.collectionServiceType)
    }, [])

    const getTemperatureControlledValues = useCallback((lane: AccountLane) => {
        let result = {}

        if (showFTL(lane.transportModeRoad)) {
            result = {
                ...result,
                isFtlPositioningTemperatureControlled: getLabel('AccountLane.isFtlPositioningTemperatureControlled'),
            }
        }

        if (showLTL(lane.transportModeRoad)) {
            result = {
                ...result,
                isLtlPositioningTemperatureControlled: getLabel('AccountLane.isLtlPositioningTemperatureControlled'),
            }
        }

        return result
    }, [getLabel])

    const config = useMemo(() => {
        return getConfig({
            getLabel,
            getAllowedValues,
            addRowMenuOption: true,
            countryParams,
            contactParams,
            locationAirportParams,
            userParams,
            isNotDeliveryByRoad,
            isNoTransportModeRoad,
            isNotCollection,
            getTemperatureControlledValues,
            customerExecutionParams,
        })
    }, [
        getLabel,
        getAllowedValues,
        countryParams,
        contactParams,
        locationAirportParams,
        userParams,
        isNotDeliveryByRoad,
        isNoTransportModeRoad,
        isNotCollection,
        getTemperatureControlledValues,
        customerExecutionParams,
    ])

    const edited = !isEqual(filterOptions?.filteredData, value)
    const filterContext = useMemo(() => {
        return {
            ...filterOptions,
            config: filterConfig,
            edited,
        }
    }, [
        filterOptions,
        edited,
    ])

    return (
        <FilterContext.Provider value={filterContext}>
            <SkyNetSpreadSheetControlPanel
                onSave={handleSave}
                onCancel={onCancel}
                edited={edited}
            >
                <DataContentWrapper
                    isEmpty={!filterByAccount.accountId}
                    emptyDataReplacer="Please select the account to add new lanes"
                >
                    <SkyNetSpreadSheet
                        config={config}
                        value={value}
                        onChange={setValue}
                    />
                </DataContentWrapper>
            </SkyNetSpreadSheetControlPanel>
        </FilterContext.Provider>
    )
}

export default AccountLaneSpreadsheet
